import React, { useContext, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styles from './LoginPage.module.css'
import authService from '../services/authService'
import { SellerModel } from '../models/SellerModel';
import { SellerAccountModel } from '../models/SellerAccountModel';
import { useAppContext } from '../context/AppContext';
import { useReferenceDataContext } from '../context/ReferenceDataContext';
import timezoneService from '../services/timezoneService';
import itemService from '../services/itemService';
import ReCAPTCHA from 'react-google-recaptcha';

const LoginPage = () => {

  const { setSeller, setSellerAccount } = useAppContext();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [captchaValue, setCaptchaValue] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const navigate = useNavigate();

  const handleLogin = () => {
    const login = async () => {
      const loginResult = await authService.login(email, password, captchaValue!);
      if (loginResult.loginState === "Success" && loginResult.seller && loginResult.sellerAccount) {
        setSeller(new SellerModel(loginResult.seller));
        setSellerAccount(new SellerAccountModel(loginResult.sellerAccount));
        navigate("/");
      } else {

        recaptchaRef.current?.reset();
        setCaptchaValue(null);

        if (loginResult.loginState === "InvalidLoginDetails") {
          setError('Invalid email or password.');
        } else if (loginResult.loginState === "InvalidReCaptchaToken") {
          setError('Invalid reCaptcha token.');
        } else if (loginResult.loginState === "Suspended") {
          setError('Your account has been suspended.');
        } else {
          setError('Failed to login');
        }
      }
      setIsSubmitting(false);
    }
    if (email && password) {
      if (!captchaValue) {
        setError('Please complete the reCAPTCHA verification.');
        return;
      }
      setIsSubmitting(true);
      login();
    } else {
      setError('Please enter your username and password.');
    }
  };

  const handleCaptchaChange = (value: string | null): void => {
    setCaptchaValue(value);
    console.log('reCaptcha token:', value);
  };

  const handleCaptchaExpired = (): void => {
    setCaptchaValue(null);
    setError('reCAPTCHA expired. Please verify again.');
  };

  const handleCaptchaError = (): void => {
    setCaptchaValue(null);
    setError('reCAPTCHA error occurred. Please try again.');
  };

  return (
    <div className={styles.loginPageContainer}>
      <div className={styles.loginImage}>
        <img src="/images/login.jpg"></img>
      </div>
      <div className={styles.formSection}>
        <div className={styles.formSignin}>
          <div className='d-flex justify-content-lg-center'>
            <img className={styles.loginLogo} src="/images/tahpi-plus.png" alt=""></img>
          </div>
          <h1 className={`h3 mb-4 text-center ${styles.title}`}>Seller Console</h1>
          <div className={`${styles.formFloatingZindex} form-floating`}>
            <input type="email" className="form-control" placeholder="name@example.com" value={email} onChange={(e) => setEmail(e.target.value)}></input>
            <label>Email address</label>
          </div>
          <div className={`${styles.formFloatingZindex} form-floating`}>
            <input type="password" className="form-control" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)}></input>
            <label>Password</label>
          </div>
          <Link to="/forgotPassword" className="text-decoration-none d-block mb-3 text-small text-dark mt-2">Forgot password?</Link>
          <ReCAPTCHA ref={recaptchaRef} sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ""} onChange={handleCaptchaChange} onExpired={handleCaptchaExpired} onError={handleCaptchaError} />
          {error && <div className="alert alert-danger mt-2" role="alert">{error}</div>}
          <button className="btn btn-lg btn-dark w-100 py-2 my-3 rounded-0" onClick={handleLogin} disabled={isSubmitting}>
            {isSubmitting ? (<><span className={`spinner-border ${styles.formSigninSpinner}`} role="status" aria-hidden="true"></span> Logging In ...</>) : ("Login")}
          </button>
          <div className="d-flex align-items-center mb-3">
            <hr className="flex-grow-1" />
            <span className="mx-2">OR</span>
            <hr className="flex-grow-1" />
          </div>
          <p className="text-decoration-none d-block mb-2 text-small text-secondary mt-2">Don't have an account?</p>
          <Link to="/signup" className="btn btn-lg btn-outline-dark w-100 py-2 rounded-0">Sign up</Link>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
